import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { motion } from 'framer-motion'
import React, { useRef, useEffect } from 'react'
import FocusLock from 'react-focus-lock'
import styled from 'styled-components'
import { Action } from 'modules/auth/abilities'
import { useAuthentication } from 'modules/auth/useAuthentication'
import { useCan } from 'modules/auth/useCan'
import { useCurrentUser } from 'modules/auth/useCurrentUser'
import { NavMenuItem } from './NavMenuItem'

const variants = {
  menuOpen: {
    x: '0'
  },
  menuClosed: {
    x: '100%'
  }
}

const listVariants = {
  menuOpen: {
    transition: { staggerChildren: 0.02, delayChildren: 0.33 }
  },
  menuClosed: {
    transition: { staggerChildren: 0.02, staggerDirection: -1 }
  }
}

const Container = styled(motion.div)`
  background: black;
  color: white;
  position: fixed;
  top: 0;
  bottom: 0;
  right: 0;
  width: 100vw;
  z-index: 1000;

  --main-links-padding: 60px;
  --item-padding: 24px;
`

const Content = styled(motion.div)`
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  height: 100%;

  &:before {
    content: '';
    background: black;
    height: var(--header-height);
    width: 100%;
    position: absolute;
    z-index: 9;
    border-bottom: 1px solid white;
  }
`

const Links = styled.div`
  text-align: right;
  max-width: var(--max-column-width);
  margin: 0 auto;
`

const MainLinksContainer = styled.div`
  border-bottom: 1px solid white;
  padding: calc(var(--header-height) + var(--main-links-padding)) 0 var(--main-links-padding) 0;
  margin-bottom: calc(var(--main-links-padding) - var(--item-padding));
`

const SecondaryLinksContainer = styled.div`
  padding-bottom: 60px;
`

interface Props {
  menuOpen: boolean
}
export const Nav = ({ menuOpen }: Props) => {
  const contentRef = useRef<HTMLDivElement>(null)
  const { can } = useCan()
  const { user, ability } = useCurrentUser()
  const { authenticationService } = useAuthentication()

  useEffect(() => {
    if (contentRef.current) {
      if (menuOpen) {
        disableBodyScroll(contentRef.current)
      } else {
        enableBodyScroll(contentRef.current)
      }
    }

    return () => {
      clearAllBodyScrollLocks()
    }
  }, [menuOpen, contentRef])

  return (
    <FocusLock disabled={!menuOpen}>
      <Container variants={variants} transition={{ ease: [0.25, 0.1, 0.25, 1], duration: 0.3 }}>
        <Content ref={contentRef} variants={listVariants}>
          <MainLinksContainer>
            <Links>
              {can('accessAdmin') && <NavMenuItem href="/admin" title="Admin" type="anchor" />}
              {!can('accessAdmin') && ability.can(Action.Create, 'BrandEditSuggestion') && (
                <NavMenuItem href="/brandmin" title="Admin" type="anchor" />
              )}

              <NavMenuItem href="/brands" title="Directory" type="anchor" />
              <NavMenuItem href="/stories" title="Stories" type="anchor" />
              <NavMenuItem href="/thingdrop" title="Thingdrop" type="anchor" />

              {!can('accessMember') && (
                <>
                  <NavMenuItem href="/about" title="About" type="anchor" />
                  <NavMenuItem
                    title="Sign up"
                    type="button"
                    onClick={() => {
                      authenticationService.send({ type: 'SHOW_MODAL', mode: 'register', analyticsContext: 'hamburger' })
                    }}
                  />
                </>
              )}

              {can('accessMember') && (
                <>
                  <NavMenuItem href={`/${user?.username}`} title="My Profile" type="anchor" />
                </>
              )}
            </Links>
          </MainLinksContainer>

          <SecondaryLinksContainer>
            <Links>
              {can('accessMember') && (
                <>
                  <NavMenuItem variant="small" href="/about" title="About" type="anchor" />
                  <NavMenuItem variant="small" href="/settings" title="Settings" type="anchor" />
                  <NavMenuItem
                    variant="small"
                    title="Log out"
                    type="button"
                    onClick={() => {
                      authenticationService.send({ type: 'SIGN_OUT' })
                    }}
                  />
                </>
              )}

              {!can('accessMember') && (
                <NavMenuItem
                  variant="small"
                  title="Sign in"
                  type="button"
                  onClick={() => {
                    authenticationService.send({ type: 'SHOW_MODAL', mode: 'login', analyticsContext: 'hamburger' })
                  }}
                />
              )}

              <NavMenuItem variant="small" href="/submit-brand" title="Submit a brand" type="anchor" />
            </Links>
          </SecondaryLinksContainer>
        </Content>
      </Container>
    </FocusLock>
  )
}
